/**
 * Cloud API adapter — wraps the shared checker-ui API client with Bearer token
 * auth and cloud-specific convenience methods.
 *
 * Instead of patching checker's api.ts at build time, we use the createApiClient
 * factory from @ensafely/checker-ui with cloud configuration.
 *
 * All types and the factory are re-exported from the upstream module so that
 * UI library code (which imports from '@/lib/api') finds everything it needs.
 *
 * Note: We import from the UI library's source api.ts directly (not via the
 * @ensafely/checker-ui barrel) because the barrel renames some types
 * (e.g. AlertEvent → ApiAlertEvent) while internal UI code expects the
 * original names.
 */

// Re-export everything from the upstream API module (types, factory, api default).
// The explicit `api` export below shadows the default `api` from this wildcard.
export * from '../../../../../ui/src/lib/api'

import { createApiClient } from '../../../../../ui/src/lib/api'
import { checkResponseVersion } from '../../../../../ui/src/lib/version-check'
import { clearToken, getToken } from './authToken'

/**
 * API error that includes the HTTP status code.
 * Thrown by cloud API methods (get/post/put/patch/del) so callers can
 * distinguish error types (e.g. 501 Not Implemented vs 400 Bad Request).
 */
export class ApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * The shared checker API client configured for cloud: Bearer token auth,
 * JWT-based token retrieval, and 401 redirect to /login.
 */
const checkerApi = createApiClient({
  authMode: 'bearer',
  getAuthToken: () => getToken(),
  onUnauthorized: () => {
    clearToken()
    window.location.href = '/login'
  },
})

/**
 * Like the shared request(), but throws ApiError with the HTTP status code
 * attached. This lets callers inspect error.status for fine-grained handling.
 */
async function cloudRequest<T>(path: string, options?: RequestInit): Promise<T> {
  const token = getToken()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options?.headers as Record<string, string>),
  }
  const res = await fetch(path, { ...options, headers })
  // Check for version changes on every API response
  checkResponseVersion(res)
  if (res.status === 401) {
    clearToken()
    window.location.href = '/login'
    throw new ApiError(401, 'Unauthorized')
  }
  if (!res.ok) {
    const contentType = res.headers.get('content-type') || ''
    if (contentType.includes('text/html')) {
      throw new ApiError(res.status, 'Server error — endpoint not available')
    }
    const body = await res.text()
    let message = body || res.statusText
    try {
      const parsed = JSON.parse(body)
      if (parsed.error) message = parsed.error
    } catch {
      if (!message || message.trim() === '') {
        message = res.statusText || `Request failed (${res.status})`
      }
    }
    throw new ApiError(res.status, message)
  }
  return res.json()
}

/**
 * Extended API object: all of checker's API methods + cloud convenience methods.
 *
 * Checker pages import `api` and get all their usual methods (getChecks, etc.).
 * Cloud SaaS pages also import `api` and use the generic get/post/put/patch/del
 * methods for billing, team, account endpoints.
 */
export const api = {
  ...checkerApi,

  // Generic methods for cloud SaaS pages (billing, team, account)
  get: <T>(path: string) => cloudRequest<T>(`/api${path}`),
  post: <T>(path: string, body: unknown) =>
    cloudRequest<T>(`/api${path}`, { method: 'POST', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    cloudRequest<T>(`/api${path}`, { method: 'PUT', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    cloudRequest<T>(`/api${path}`, { method: 'PATCH', body: JSON.stringify(body) }),
  del: <T>(path: string) =>
    cloudRequest<T>(`/api${path}`, { method: 'DELETE' }),
}
